import React, { useState, forwardRef, useImperativeHandle } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  FormControl,
  TextField,
} from "@mui/material";
import Select, { components } from "react-select";
import ElectionData from "../../data/ElectionData";

const VotingInfo = forwardRef(
  ({ selectedFilters, setSelectedFilters }, ref) => {
    const [optionData, setOptionData] = useState([]);
    const [selectedAgeFilter, setSelectedAgeFilter] = useState(null);
    const [ageInputValue, setAgeInputValue] = useState("");

    const advancedFilters = [
      // { label: "Begins with", value: "startsWith" },
      { label: "Equals", value: "equals" },
      { label: "Between (Format Age-Age)", value: "between" },
      { label: "Less Than", value: "lessThan" },
      { label: "Greater Than", value: "greaterThan" },
    ];

    const handleChange = (filterName, newValue, selectedOption, filterType) => {
      if (filterName !== "selectedAge" && filterName !== "selectedAgeFilterType") {
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          [filterName]: newValue,
        }));
      } else if (filterName === "selectedAgeFilterType") {
        // Only update the filter type, not the value
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          selectedAge: {
            value: ageInputValue,
            label: ageInputValue,
            filterType: filterType || "startsWith",
          },
        }));
      } else if (filterName === "selectedAge") {
        // Update the age input value
        setAgeInputValue(newValue);
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          [filterName]: {
            value: newValue,
            label: newValue,
            filterType: selectedFilters.selectedAge?.filterType || "startsWith",
          },
        }));
      }

      if (filterName === "selectedElectionOperator") {
        if (newValue === "At Least") {
          setSelectedFilters((prevFilters) => ({
            ...prevFilters,
            selectedElectionsAtLeast: 1,
          }));
        } else {
          setSelectedFilters((prevFilters) => ({
            ...prevFilters,
            selectedElectionsAtLeast: null,
          }));
        }
      }
    };

    function getUniqueOptions(filter) {
      switch (filter) {
        case "Status":
          return ["Active", "Inactive"];
        case "ElectionsOperator":
          return ["All", "Any", "At Least"];
        case "Age":
          return [...new Set(optionData.Age)];
        case "Elections":
          return [...new Set(ElectionData.map((item) => item.election))];
        default:
          return [];
      }
    }

    useImperativeHandle(ref, () => ({
      handleRefreshFilter() {
        setSelectedAgeFilter(null);
        setAgeInputValue("");
      },
    }));

    const MenuList = (props) => {
      const { maxHeight, children, innerProps } = props;
      return (
        <components.Menu {...props}>
          <div style={{ maxHeight, overflowY: "auto" }}>{children}</div>
        </components.Menu>
      );
    };

    return (
      <div>
        <Card elevation={3} className="m-2 sm:m-4">
          <CardContent>
            <Typography variant="h6" className="mb-3 pt-3">
              Voting
            </Typography>

            <Grid container spacing={2}>
              {/* Status - Full Width */}
              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <Autocomplete
                    options={getUniqueOptions("Status")}
                    value={selectedFilters[`selectedVoterStatus`]}
                    onChange={(_, newValue) =>
                      handleChange(`selectedVoterStatus`, newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        variant="outlined"
                        margin="normal"
                      />
                    )}
                    multiple
                  />
                </FormControl>
              </Grid>

              {/* Select Age - Left Column */}
              <Grid item xs={6} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <Autocomplete
                    options={advancedFilters.map(item => item.label)}
                    value={selectedAgeFilter?.label || null}
                    onChange={(_, newValue) => {
                      const selectedOption = advancedFilters.find(item => item.label === newValue);
                      if (selectedOption) {
                        setSelectedAgeFilter(selectedOption);
                        handleChange(
                          "selectedAgeFilterType",
                          selectedOption.label,
                          selectedOption,
                          selectedOption.value
                        );
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Age"
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Search for Age - Right Column */}
              <Grid item xs={6} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    label="Search for Age"
                    fullWidth
                    variant="outlined"
                    value={ageInputValue}
                    onChange={(e) =>
                      handleChange(
                        "selectedAge",
                        e.target.value
                      )
                    }
                  />
                </FormControl>
              </Grid>

              {/* Elections Operator - Left Column */}
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <Autocomplete
                    options={getUniqueOptions("ElectionsOperator")}
                    value={selectedFilters.selectedElectionOperator}
                    onChange={(_, newValue) =>
                      handleChange("selectedElectionOperator", newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Elections Operator"
                        variant="outlined"
                      />
                    )}
                  />
                  {selectedFilters.selectedElectionOperator === "At Least" && (
                    <TextField
                      label="At Least Elections"
                      variant="outlined"
                      margin="normal"
                      type="number"
                      value={selectedFilters.selectedElectionsAtLeast}
                      onChange={(e) =>
                        handleChange("selectedElectionsAtLeast", e.target.value)
                      }
                    />
                  )}
                </FormControl>
              </Grid>

              {/* Elections - Right Column */}
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <Autocomplete
                    options={getUniqueOptions("Elections")}
                    value={selectedFilters.selectedElection}
                    onChange={(_, newValue) =>
                      handleChange("selectedElection", newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Elections"
                        variant="outlined"
                      />
                    )}
                    multiple
                  />
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    );
  }
);

export default VotingInfo;
